import { useAggregatedMetrics } from '../hooks/use-metrics-summary';
import { useCountriesMap } from '@/lib/hooks/use-countries';
import { MetricCard } from '@/components/organisms/cards';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import { Badge } from '@/components/atoms/badge';
import { Skeleton } from '@/components/atoms/skeleton';
import { EmptyState } from '@/components/organisms/empty-state';
import { useTranslation } from 'react-i18next';
import { Eye, Search, Package, Globe, TrendingUp, Hash } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SubscriptionGate } from '@/lib/subscription';

export function MetricsContainer() {
  const { t } = useTranslation();
  const { data: aggregated, rawData, isLoading, error } = useAggregatedMetrics();
  const { getCountryFlag, getCountryName } = useCountriesMap();

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">{t('metrics.error')}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="gap-4 py-4">
              <div className="px-4 space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-8 w-32" />
              </div>
            </Card>
          ))}
        </div>
        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardContent className="pt-6">
              <Skeleton className="h-64 w-full" />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <Skeleton className="h-64 w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const hasData = rawData?.productMetrics && rawData.productMetrics.length > 0;

  if (!hasData) {
    return (
      <EmptyState
        title={t('metrics.noData')}
        description={t('metrics.noDataDescription')}
        icon={TrendingUp}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <MetricCard
          title={t('metrics.totalViews')}
          value={(aggregated?.totalViews ?? 0).toLocaleString()}
          icon={Eye}
        />
        <MetricCard
          title={t('metrics.searchAppearances')}
          value={(aggregated?.totalSearchAppearances ?? 0).toLocaleString()}
          icon={Search}
        />
        <MetricCard
          title={t('metrics.trackedProducts')}
          value={(aggregated?.totalProducts ?? 0).toLocaleString()}
          icon={Package}
        />
      </div>

      {/* Advanced Metrics - Gated by Subscription */}
      <SubscriptionGate feature="advancedMetrics">
        {/* Top Search Terms & Countries */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Top Search Terms */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Hash className="size-5" />
                {t('metrics.topSearchTerms')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {aggregated?.topSearchTerms && aggregated.topSearchTerms.length > 0 ? (
                <div className="space-y-3">
                  {aggregated.topSearchTerms.map((item, index) => (
                    <div
                      key={item.term}
                      className="flex items-center justify-between py-2 border-b last:border-0"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-muted-foreground w-6">#{index + 1}</span>
                        <span className="font-medium">{item.term}</span>
                      </div>
                      <Badge variant="secondary">{item.count} {t('metrics.searches')}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center h-32 text-muted-foreground">
                  {t('metrics.noSearchTerms')}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top Countries */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="size-5" />
                {t('metrics.topCountries')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {aggregated?.topCountries && aggregated.topCountries.length > 0 ? (
                <div className="space-y-3">
                  {aggregated.topCountries.map((item, index) => {
                    const flag = getCountryFlag(item.country);
                    const name = getCountryName(item.country);
                    return (
                      <div
                        key={item.country}
                        className="flex items-center justify-between py-2 border-b last:border-0"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-muted-foreground w-6">#{index + 1}</span>
                          {flag && <span className="text-lg">{flag}</span>}
                          <span className="font-medium">{name}</span>
                        </div>
                        <Badge variant="secondary">{item.views} {t('metrics.views')}</Badge>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center justify-center h-32 text-muted-foreground">
                  {t('metrics.noCountryData')}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Product Performance */}
        <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="size-5" />
            {t('metrics.productPerformance')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {aggregated?.productPerformance && aggregated.productPerformance.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">
                      {t('metrics.product')}
                    </th>
                    <th className="text-right py-3 px-2 text-sm font-medium text-muted-foreground">
                      {t('metrics.views')}
                    </th>
                    <th className="text-right py-3 px-2 text-sm font-medium text-muted-foreground">
                      {t('metrics.searches')}
                    </th>
                    <th className="text-right py-3 px-2 text-sm font-medium text-muted-foreground">
                      {t('metrics.avgPosition')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {aggregated.productPerformance.map((product) => (
                    <tr key={product.productId} className="border-b last:border-0 hover:bg-muted/50">
                      <td className="py-3 px-2">
                        <Link
                          to={`/products/${product.productId}`}
                          className="text-primary hover:underline font-mono text-sm"
                        >
                          {product.productId}
                        </Link>
                      </td>
                      <td className="py-3 px-2 text-right font-medium">
                        {product.totalViews.toLocaleString()}
                      </td>
                      <td className="py-3 px-2 text-right font-medium">
                        {product.totalSearches.toLocaleString()}
                      </td>
                      <td className="py-3 px-2 text-right">
                        {product.avgPosition > 0 ? (
                          <Badge
                            variant={Math.round(product.avgPosition) <= 3 ? 'default' : 'outline'}
                            className={
                              Math.round(product.avgPosition) === 1
                                ? 'bg-green-500'
                                : Math.round(product.avgPosition) <= 3
                                  ? 'bg-blue-500'
                                  : ''
                            }
                          >
                            #{Math.round(product.avgPosition)}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex items-center justify-center h-32 text-muted-foreground">
              {t('metrics.noProductData')}
            </div>
          )}
        </CardContent>
      </Card>
      </SubscriptionGate>
    </div>
  );
}
